import { FormikQuote, type QuoteFormikType, type QuoteSubmitType } from '@core/formik/quote';
import type { AddressWireFormat } from '@core/types/api/Address/Address';
import Enums from '@core/types/api/Enums';
import type { QuoteConfig } from '@core/types/api/QuoteConfig/QuoteConfig';
import type { UserInfo } from '@core/types/api/UserInfo/UserInfo';
import type { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { FormikAddress } from '@core/utils/formikHelpers';
import { areQuoteConfigProductsSynced, defaultQuoteExpiration } from '@core/utils/quoteHelpers';

export type QuickProjectMode = 'new' | 'existing';

export type QuickQuoteAddress = AddressWireFormat;

export const emptyQuickQuoteAddress = (): QuickQuoteAddress => FormikAddress(undefined);

export type QuoteDrawerFormikType = QuoteFormikType & {
  quickProjectMode: QuickProjectMode;
  quickAddress: QuickQuoteAddress;
};

export type QuoteDrawerState =
  | 'quoteLoading'
  | 'edit'
  | 'violationsLoading'
  | 'reviewViolations'
  | 'confirmApproved'
  | 'send'
  | 'error';

export const makeDefaultFormikQuote = ({
  userInfo,
  submitType,
  isMoneySwitchOn,
  marginConfiguration,
  defaultMargins,
}: {
  userInfo: UserInfo;
  submitType: QuoteSubmitType;
  isMoneySwitchOn: boolean;
  marginConfiguration: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
}): QuoteFormikType =>
  FormikQuote(
    {
      expirationDate: defaultQuoteExpiration(userInfo.tenant),
      user: userInfo.userIfRoles([Enums.RoleName.Salesperson]),
    },
    userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagDispatchCustomer]),
    submitType,
    isMoneySwitchOn,
    marginConfiguration,
    defaultMargins,
  );

/** If there is no selected quote config, everything is considered synced. */
export const isQuoteConfigSynced = (
  curQuoteConfig: QuoteConfig | undefined,
  formikAdditionalProductsByPlants: QuoteFormikType['additionalProductsByPlants'],
  formikTerms: QuoteFormikType['terms'],
): { areTermsSynced: boolean; areProductsSynced: boolean } => {
  if (curQuoteConfig === undefined) {
    return {
      areProductsSynced: true,
      areTermsSynced: true,
    };
  }

  const areProductsSynced = areQuoteConfigProductsSynced(
    curQuoteConfig,
    formikAdditionalProductsByPlants,
  );

  return {
    areProductsSynced,
    areTermsSynced: curQuoteConfig.terms === formikTerms,
  };
};
